import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Flame, Users, Sparkles, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

function monthLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", year: "numeric" }).toUpperCase();
}

export default function Founders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/founders").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-[40vh] flex items-center justify-center text-[#666]">Loading the wall…</div>;
  }
  if (!data) return null;

  const founders = data.founders || [];
  const referred = data.referred || [];
  const seatsRemaining = Math.max(0, data.total_seats - data.seats_taken);

  return (
    <div className="space-y-16" data-testid="founders-root">
      <header className="text-center max-w-3xl mx-auto">
        <p className="label-eyebrow">The wall</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">
          The <span className="text-[#c89f45]">Founders</span>.
        </h1>
        <p className="text-[#aaa] mt-6 text-base leading-relaxed">
          The first 100 people to commit to running their own boardroom. Names on the wall,
          their friends seated behind them. Lifetime Pro, lifetime credit.
        </p>
        <div className="mt-8 inline-flex items-center gap-6 text-xs font-mono text-[#888]">
          <span><span className="text-[#c89f45]">{data.seats_taken}</span> / {data.total_seats} CLAIMED</span>
          <span className="text-[#333]">·</span>
          <span><span className="text-[#c89f45]">{seatsRemaining}</span> SEATS LEFT</span>
        </div>
        {seatsRemaining > 0 && (
          <div className="mt-6">
            <Link to="/pricing" data-testid="founders-cta-pricing">
              <Button className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-11 px-5 font-medium">
                <Flame size={14} className="mr-2" /> Claim your seat — $99 once
              </Button>
            </Link>
          </div>
        )}
      </header>

      <div className="gold-divider" />

      {/* Founders grid */}
      {founders.length === 0 ? (
        <div className="surface rounded-md p-16 text-center max-w-2xl mx-auto" data-testid="founders-empty">
          <Crown size={24} className="text-[#c89f45] mx-auto mb-4" />
          <p className="font-serif text-2xl">The wall is awaiting its first name.</p>
          <p className="text-sm text-[#888] mt-2">Be Founder #1.</p>
        </div>
      ) : (
        <section>
          <p className="label-eyebrow text-center">Charter members</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-testid="founders-grid">
            {founders.map((f) => (
              <div
                key={f.founder_number}
                className="surface rounded-md p-6 grain-overlay border-[#c89f45]/20 hover:border-[#c89f45]/60 transition-colors"
                data-testid={`founder-${f.founder_number}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs text-[#c89f45] tracking-widest">
                    {`#${String(f.founder_number).padStart(3, "0")}`}
                  </span>
                  {f.referral_redemptions > 0 && (
                    <span className="flex items-center gap-1 text-[10px] font-mono text-[#c89f45] bg-[#c89f45]/10 px-2 py-0.5 rounded uppercase tracking-widest">
                      <Users size={10} /> +{f.referral_redemptions}
                    </span>
                  )}
                </div>
                <p className="font-serif text-2xl mt-4 leading-tight">{f.name}</p>
                <p className="font-mono text-[10px] text-[#666] mt-3 tracking-widest">{monthLabel(f.joined_at)}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Referred members */}
      {referred.length > 0 && (
        <section className="max-w-4xl mx-auto" data-testid="referred-section">
          <div className="text-center">
            <p className="label-eyebrow">Behind them</p>
            <h2 className="font-serif text-3xl mt-3">Seated by a Founder</h2>
            <p className="text-sm text-[#888] mt-3">
              Joined Pro or Syndicate via a Founder's personal referral code.
            </p>
          </div>
          <ul className="mt-8 space-y-2">
            {referred.map((r, i) => (
              <li
                key={r.id || `${r.referred_by || "unknown"}-${r.name || "member"}-${i}`}
                className="surface rounded-md p-4 flex items-center justify-between"
                data-testid={`referred-${r.id || i}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <Sparkles size={14} className="text-[#c89f45] shrink-0" />
                  <p className="text-base">{r.name}</p>
                  <span className="text-xs text-[#666]">via</span>
                  <span className="text-sm text-[#c89f45]">{r.referred_by}</span>
                </div>
                <span className="text-[10px] font-mono text-[#666] tracking-widest">{monthLabel(r.joined_at)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="text-center text-xs text-[#666] font-mono pt-6 pb-4">
        <p>Manage your wall visibility in <Link to="#" onClick={(e) => { e.preventDefault(); document.querySelector("[data-testid='open-settings']")?.click(); }} className="text-[#888] hover:text-[#c89f45]">Settings</Link></p>
      </div>
    </div>
  );
}
